<template>
  <div class="mobile-map-wrapper">
    <div class="mobile-map-container" ref="mapContainerRef"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'

// 移动端专用的地图缓存
let isChinaMapRegistered = false
let chinaGeoJsonCache = null

export default {
  name: 'MobileMapComponent',
  props: {
    stations: {
      type: Array,
      default: () => []
    }
  },
  emits: ['station-click', 'province-click'],
  data() {
    return {
      chartInstance: null,
      selectedProvince: '吉林省',
      selectedStation: null,
      animationTimer: null
    }
  },
  created() {
    // Non-reactive state to prevent infinite zoom loops
    this._currentCenter = [126.192, 43.723]
    this._currentZoom = 6
  },
  watch: {
    stations: {
      deep: true,
      handler() {
        if (this.chartInstance) {
          this.updateChart()
        }
      }
    }
  },
  async mounted() {
    await this.loadGeoJson()
    this.initChart()
    window.addEventListener('resize', this.handleResize)

    // 通知父组件默认选择
    const jilin = chinaGeoJsonCache?.features.find(f => f.properties.name === '吉林省')
    const jilinCenter = jilin ? this.getFeatureBounds(jilin).center : [126.192, 43.723]
    this.$emit('province-click', { name: '吉林省', center: jilinCenter })
  },
  beforeUnmount() {
    if (this.animationTimer) {
      clearTimeout(this.animationTimer)
      this.animationTimer = null
    }
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    async loadGeoJson() {
      if (!isChinaMapRegistered) {
        try {
          // 1. 优先尝试根目录加载 (适配生产环境 www.geofusion.autosquare.org.cn)
          const res = await axios.get('/china.json')
          chinaGeoJsonCache = Object.freeze(res.data)
          echarts.registerMap('china', JSON.parse(JSON.stringify(chinaGeoJsonCache)))
          isChinaMapRegistered = true
        } catch (err) {
          // 2. 如果失败，尝试带前缀的路径 (适配本地开发环境 /geofusion/)
          try {
            const res = await axios.get('/geofusion/china.json')
            chinaGeoJsonCache = Object.freeze(res.data)
            echarts.registerMap('china', JSON.parse(JSON.stringify(chinaGeoJsonCache)))
            isChinaMapRegistered = true
          } catch (err2) {
             console.error('[MobileMapComponent] 本地 GeoJSON 加载失败，尝试网络回退:', err2)
             // 3. 最后尝试网络资源兜底
            try {
              const res = await axios.get(
                'https://geo.datav.aliyun.com/areas_v3/bound/100000_full.json'
              )
              chinaGeoJsonCache = Object.freeze(res.data)
              echarts.registerMap('china', JSON.parse(JSON.stringify(chinaGeoJsonCache)))
              isChinaMapRegistered = true
            } catch (err3) {
              console.error('[MobileMapComponent] 所有 GeoJSON 加载均失败:', err3)
            }
          }
        }
      }

      if (chinaGeoJsonCache && this.selectedProvince) {
        const feature = chinaGeoJsonCache.features.find(
          (f) => f.properties.name === this.selectedProvince
        )
        if (feature) {
          const bounds = this.getFeatureBounds(feature)
          if (bounds) {
            this._currentCenter = bounds.center
            this._currentZoom = bounds.zoom
            this.currentZoomLevel = Math.round(bounds.zoom * 10) / 10
          }
        }
      }
    },

    initChart() {
      const el = this.$refs.mapContainerRef
      if (!el) return
      this.chartInstance = echarts.init(el)
      this.renderChart()
      this.bindEvents()
    },

    renderChart() {
      if (!this.chartInstance) return
      const option = this.buildOption()
      this.chartInstance.setOption(option, { notMerge: true, lazyUpdate: true })
    },

    updateChart() {
      if (this.chartInstance) {
        this.chartInstance.clear()
        this.renderChart()
      }
    },

    buildOption() {
      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          borderColor: '#00f0ff',
          borderWidth: 1,
          textStyle: { color: '#fff', fontSize: 12 },
          formatter: (params) => {
            if (params.componentType === 'series' && params.seriesType === 'effectScatter') {
              if (!params.data) return null
              const statusMap = {
                normal: '<span style="color:#10b981">● 正常</span>',
                yellow: '<span style="color:#eab308">● 黄色预警</span>',
                orange: '<span style="color:#f97316">● 橙色预警</span>',
                red: '<span style="color:#ef4444">● 红色预警</span>'
              }
              const s = params.data.status || 'normal'
              return `
                <div style="padding: 4px 2px;">
                  <strong style="color: #00f0ff">${params.name || '未知站点'}</strong><br/>
                  状态: ${statusMap[s] || s}<br/>
                  GPS: ${params.value && params.value.length >= 2 ? params.value[0].toFixed(4) : '--'}, 
                       ${params.value && params.value.length >= 2 ? params.value[1].toFixed(4) : '--'}
                </div>
              `
            }
            return null
          }
        },
        geo: {
          map: 'china',
          roam: true,
          selectedMode: 'single',
          zoom: this._currentZoom,
          center: this._currentCenter,
          itemStyle: {
            areaColor: '#0f172a',
            borderColor: '#3b82f6',
            borderWidth: 1
          },
          label: {
            show: true,
            color: 'rgba(255, 255, 255, 0.25)',
            fontSize: 9
          },
          emphasis: {
            itemStyle: {
              areaColor: '#1e3a5f',
              borderColor: '#00f0ff',
              borderWidth: 2
            },
            label: {
              show: true,
              color: '#fff',
              fontSize: 11
            }
          },
          select: {
            itemStyle: {
              areaColor: '#1e40af',
              borderColor: '#00f0ff',
              borderWidth: 2,
              shadowBlur: 15,
              shadowColor: 'rgba(0, 240, 255, 0.4)'
            },
            label: {
              show: true,
              color: '#fff',
              fontWeight: 'bold',
              fontSize: 12
            }
          },
          regions: this.selectedProvince ? [{ name: this.selectedProvince, selected: true }] : []
        },
        series: [
          {
            name: '监测站',
            type: 'effectScatter',
            coordinateSystem: 'geo',
            data: this.stations,
            symbolSize: 12,
            showEffectOn: 'render',
            rippleEffect: {
              brushType: 'stroke',
              scale: 2,
              period: 6
            },
            progressive: 100,
            progressiveThreshold: 200,
            itemStyle: {
              color: (params) => {
                const status = (params && params.data) ? params.data.status : 'normal'
                return this.getStatusColor(status)
              },
              shadowBlur: 8,
              shadowColor: 'rgba(0, 0, 0, 0.3)'
            },
            zlevel: 1,
            label: {
              formatter: '{b}',
              position: 'right',
              show: false,
              color: '#f8fafc',
              fontSize: 10,
              textBorderColor: '#000',
              textBorderWidth: 1
            }
          }
        ]
      }
    },

    getStatusColor(status) {
      const map = {
        red: '#ef4444',
        orange: '#f97316',
        yellow: '#eab308',
        normal: '#10b981'
      }
      return map[status] || '#10b981'
    },

    bindEvents() {
      if (!this.chartInstance) return
      this.chartInstance.off('click')
      this.chartInstance.off('geoselectchanged')
      this.chartInstance.off('georoam')

      // 同步拖拽/缩放状态
      this.chartInstance.on('georoam', () => {
        const geoOpt = this.chartInstance.getOption().geo
        if (geoOpt && geoOpt[0]) {
          this._currentCenter = [...geoOpt[0].center]
          this._currentZoom = geoOpt[0].zoom
        }
      })

      // 站点点击
      this.chartInstance.on('click', 'series', (params) => {
        if (params.seriesType === 'effectScatter' && params.value && Array.isArray(params.value) && params.value.length >= 2) {
          const stationName = params.name
          if (this.selectedStation === stationName) {
            this.selectedStation = null
            this.zoomToProvince(this.selectedProvince || '全国')
            this.$emit('province-click', { name: this.selectedProvince || '全国' })
          } else {
            this.selectedStation = stationName
            this.$emit('station-click', {
              name: stationName,
              gps: [params.value[0], params.value[1]]
            })
          }
        }
      })

      // 省份选择
      this.chartInstance.on('geoselectchanged', (params) => {
        const selected = params.allSelected[0]
        if (selected && selected.name && selected.name.length > 0) {
          const provinceName = selected.name[0]
          if (this.selectedProvince === provinceName) {
            this.selectedProvince = null
            this.selectedStation = null
            this.chartInstance.dispatchAction({ type: 'geoUnSelect', name: provinceName })
            this.resetMapView()
            this.$emit('province-click', { name: '全国' })
          } else {
            this.selectedProvince = provinceName
            const feature = chinaGeoJsonCache.features.find(f => f.properties.name === provinceName)
            const bounds = feature ? this.getFeatureBounds(feature) : null
            this.zoomToProvince(provinceName)
            this.$emit('province-click', { name: provinceName, center: bounds ? bounds.center : null })
          }
        } else {
          this.selectedProvince = null
          this.resetMapView()
          this.$emit('province-click', { name: '全国' })
        }
      })
    },

    zoomToProvince(provinceName) {
      if (!chinaGeoJsonCache) return
      const feature = chinaGeoJsonCache.features.find(f => f.properties.name === provinceName)
      if (!feature) return
      const bounds = this.getFeatureBounds(feature)
      if (!bounds) return
      this.animateMapView(bounds.center, bounds.zoom)
    },

    getFeatureBounds(feature) {
      const coords = feature.geometry.coordinates
      let minX = Infinity, maxX = -Infinity
      let minY = Infinity, maxY = -Infinity
      let hasPoints = false

      const processCoords = (arr) => {
        if (typeof arr[0] === 'number') {
          hasPoints = true
          if (arr[0] < minX) minX = arr[0]
          if (arr[0] > maxX) maxX = arr[0]
          if (arr[1] < minY) minY = arr[1]
          if (arr[1] > maxY) maxY = arr[1]
        } else {
          arr.forEach(processCoords)
        }
      }

      processCoords(coords)
      if (!hasPoints) return null

      const center = [(minX + maxX) / 2, (minY + maxY) / 2]
      const width = maxX - minX
      const height = maxY - minY
      const paddingFactor = 0.8
      const scaleW = 75 / (width || 1)
      const scaleH = 50 / (height || 1)
      let zoom = Math.min(scaleW, scaleH) * paddingFactor
      zoom = Math.max(1.5, Math.min(zoom, 12))
      return { center, zoom }
    },

    resetMapView() {
      if (!this.chartInstance) return
      this.animateMapView([104.114129, 37.550339], 1.2)
    },

    animateMapView(targetCenter, targetZoom, duration = 350) {
      if (!this.chartInstance) return
      const el = this.$refs.mapContainerRef
      if (!el) return

      // Cancel any running animation
      if (this.animationTimer) {
        clearTimeout(this.animationTimer)
        this.animationTimer = null
        el.style.transition = 'none'
        el.style.transform = 'none'
        el.style.opacity = '1'
      }

      // Convert target geo coordinates to current pixel position
      const targetPixel = this.chartInstance.convertToPixel({ geoIndex: 0 }, targetCenter)
      if (!targetPixel) {
        // Fallback: direct jump
        this._currentCenter = [...targetCenter]
        this._currentZoom = targetZoom
        this.chartInstance.setOption({ geo: { center: targetCenter, zoom: targetZoom } }, { notMerge: false, lazyUpdate: false })
        return
      }

      const scale = targetZoom / this._currentZoom
      const cx = el.clientWidth / 2
      const cy = el.clientHeight / 2
      const tx = -scale * (targetPixel[0] - cx)
      const ty = -scale * (targetPixel[1] - cy)

      // Start GPU-accelerated CSS transition
      el.style.transition = 'none'
      el.style.transform = 'none'
      void el.offsetHeight  // force reflow

      el.style.transformOrigin = '50% 50%'
      el.style.transition = `transform ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`
      el.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`

      // After CSS animation ends, swap to real ECharts state
      this.animationTimer = setTimeout(() => {
        // Hide instantly, swap state, then quick fade-in
        el.style.transition = 'none'
        el.style.opacity = '0'
        el.style.transform = 'none'

        this._currentCenter = [...targetCenter]
        this._currentZoom = targetZoom
        if (this.chartInstance) {
          this.chartInstance.setOption({
            geo: { center: targetCenter, zoom: targetZoom }
          }, { notMerge: false, lazyUpdate: false })
        }

        // Fade in after ECharts repaints
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            el.style.transition = 'opacity 50ms ease-in'
            el.style.opacity = '1'
          })
        })

        this.animationTimer = null
      }, duration + 20)
    },

    zoomToStation(station) {
      if (!station || !station.value) return
      const [lon, lat] = station.value
      // Zoom into station with a specific level (e.g., 10)
      this.animateMapView([lon, lat], 10)
      this.selectedStation = station.name
    },

    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    }
  }
}
</script>

<style scoped>
.mobile-map-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.mobile-map-container {
  width: 100%;
  height: 100%;
  min-height: 300px;
  display: block;
  touch-action: none;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  pointer-events: auto;
  will-change: transform;
}
</style>
